import { ChangeEvent, FC, useEffect, useState, useCallback, FocusEvent, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Box, Grid, TextField } from "@mui/material"
import { debounce } from "lodash"

import { useAppDispatch, useAppSelector } from "../../../core/app/hooks"
import { setAlert } from "../../../core/app/slices/alert/alertSlice"
import { IClinicalSettings } from "../../../core/app/slices/user/user.type"
import { updateClinicalSettings } from "../../../core/app/slices/user/userApis"
import { RootState } from "../../../core/app/store"
import { UI_PERMISSION } from "../../../core/utils/access/permissionConstants"
import usePermissions from "../../../hooks/userPermission"
import { CENETER_ROW_ALIGN } from "../../../theme/theme.util"
import { UText } from "../../../ui-component"

const StagingSettings: FC = () => {
  const { hasAny } = usePermissions()
  const { clinicalSettings } = useAppSelector(
    (state: RootState) => state.userService,
  )
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [localSettings, setLocalSettings] = useState<IClinicalSettings>(
    {} as IClinicalSettings,
  )
  
  const [inputValues, setInputValues] = useState<{
    DefaultStep_length: string
    DefaultStep_angle: string
  }>({
    DefaultStep_length: "",
    DefaultStep_angle: "",
  })

  const [errors, setErrors] = useState<{
    DefaultStep_length: string
    DefaultStep_angle: string
  }>({
    DefaultStep_length: "",
    DefaultStep_angle: "",
  })

  const lengthInputRef = useRef<HTMLInputElement>(null)
  const angleInputRef = useRef<HTMLInputElement>(null)

  const debouncedSave = useCallback(
    debounce((obj: Partial<IClinicalSettings>) => {
      if (hasAny(UI_PERMISSION.UI_CLINICALSETTING_UPDATE)) {
        dispatch(updateClinicalSettings(obj)).then((res) => {
          if (res && res.payload && res.payload.response_code === 200) {
            setLocalSettings((prev) => ({ ...prev, ...obj }))
          } else {
            dispatch(
              setAlert({
                message: t("stagingSettings.saveFailed"),
                isError: true,
              }),
            )
          }
        })
      }
    }, 800),
    [hasAny, dispatch, t],
  )

  useEffect(() => {
    if (clinicalSettings) {
      setLocalSettings(clinicalSettings)
      const lengthValue = clinicalSettings.DefaultStep_length
      const angleValue = clinicalSettings.DefaultStep_angle
      setInputValues({
        DefaultStep_length: lengthValue != null ? parseFloat(lengthValue.toFixed(2)).toString() : "",
        DefaultStep_angle: angleValue != null ? parseFloat(angleValue.toFixed(1)).toString() : "",
      })
    }
  }, [clinicalSettings])

  useEffect(() => {
    const lengthInput = lengthInputRef.current
    const angleInput = angleInputRef.current

    const preventWheel = (e: WheelEvent) => {
      e.preventDefault()
    }

    if (lengthInput) {
      lengthInput.addEventListener("wheel", preventWheel, { passive: false })
    }
    if (angleInput) {
      angleInput.addEventListener("wheel", preventWheel, { passive: false })
    }

    return () => {
      if (lengthInput) {
        lengthInput.removeEventListener("wheel", preventWheel)
      }
      if (angleInput) {
        angleInput.removeEventListener("wheel", preventWheel)
      }
    }
  }, [])

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!hasAny(UI_PERMISSION.UI_CLINICALSETTING_UPDATE)) return

    const { name, value } = event.target

    setInputValues((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (!hasAny(UI_PERMISSION.UI_CLINICALSETTING_UPDATE)) return

    const { name, value } = event.target
    let numValue = parseFloat(value)

    if (isNaN(numValue) || value === "") {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }))
      return
    }

    if (name === "DefaultStep_length") {
      numValue = parseFloat(numValue.toFixed(2))
      setInputValues((prev) => ({
        ...prev,
        [name]: numValue.toString(),
      }))
    } else if (name === "DefaultStep_angle") {
      numValue = parseFloat(numValue.toFixed(1))
      setInputValues((prev) => ({
        ...prev,
        [name]: numValue.toString(),
      }))
    }

    let errorMessage = ""
    if (name === "DefaultStep_length") {
      if (numValue < 0.05 || numValue > 1.0) {
        errorMessage = t("stagingSettings.lengthRangeError")
      }
    } else if (name === "DefaultStep_angle") {
      if (numValue < 0.5 || numValue > 10.0) {
        errorMessage = t("stagingSettings.angleRangeError")
      }
    }

    setErrors((prev) => ({
      ...prev,
      [name]: errorMessage,
    }))

    if (errorMessage) return

    const originalValue = name === "DefaultStep_length" 
      ? localSettings.DefaultStep_length 
      : localSettings.DefaultStep_angle
    
    if (numValue === originalValue) return

    const obj = { [name]: numValue }
    debouncedSave(obj)
  }

  return (
    <Box sx={{ width: "640px", p: 4 }}>
      <Box sx={{ ...CENETER_ROW_ALIGN, justifyContent: "center", pb: "32px" }}>
        <UText variant={"h4"}>{t("stagingSettings.title")}</UText>
      </Box>
      <Box sx={{ mb: "16px" }}>
        <UText variant="h6" sxProp={{ width: "160px", flexShrink: 0, mb: 0 }}>
          {t("stagingSettings.description")}
        </UText>
      </Box>
      <Box sx={{ml:"16px"}}>
        <Grid>
          <Grid
            item
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <UText variant="body1" sxProp={{ width: "160px", flexShrink: 0, mb: 0 }}>
                  {t("stagingSettings.linearSize")}
                </UText>
                <TextField
                  name="DefaultStep_length"
                  type="number"
                  value={inputValues.DefaultStep_length}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  inputRef={lengthInputRef}
                  disabled={!hasAny(UI_PERMISSION.UI_CLINICALSETTING_UPDATE)}
                  error={!!errors.DefaultStep_length}
                  helperText={errors.DefaultStep_length}
                  InputProps={{
                    endAdornment: <Box sx={{ ml: 1 }}>mm</Box>,
                  }}
                  inputProps={{
                    step: "any",
                  }}
                  sx={{
                    width: "150px",
                    "& .MuiInputBase-input": {
                      color: (theme) => theme.palette.text.primary,
                    },
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]": {
                      MozAppearance: "textfield",
                    },
                  }}
                />
                <UText variant="caption" sxProp={{ color: "text.secondary", fontSize: "12px", mb: 0 }}>
                  (0.05 - 1.0)
                </UText>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <UText variant="body1" sxProp={{ width: "160px", flexShrink: 0, mb: 0 }}>
                  {t("stagingSettings.angularSize")}
                </UText>
                <TextField
                  name="DefaultStep_angle"
                  type="number"
                  value={inputValues.DefaultStep_angle}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  inputRef={angleInputRef}
                  disabled={!hasAny(UI_PERMISSION.UI_CLINICALSETTING_UPDATE)}
                  error={!!errors.DefaultStep_angle}
                  helperText={errors.DefaultStep_angle}
                  InputProps={{
                    endAdornment: <Box sx={{ ml: 1 }}>°</Box>,
                  }}
                  inputProps={{
                    step: "any",
                  }}
                  sx={{
                    width: "150px",
                    "& .MuiInputBase-input": {
                      color: (theme) => theme.palette.text.primary,
                    },
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "& input[type=number]": {
                      MozAppearance: "textfield",
                    },
                  }}
                />
                <UText variant="caption" sxProp={{ color: "text.secondary", fontSize: "12px", mb: 0 }}>
                  (0.5 - 10.0)
                </UText>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default StagingSettings
